import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navigationItems } from '../../constants/navigation';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { agencyAPI } from '../../api/agencies';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Agency {
  id: string;
  name: string;
  logo_url?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agency, setAgency] = useState<Agency | null>(null);

  useEffect(() => {
    const fetchAgencyLogo = async () => {
      if (user?.agency_id) {
        try {
          const agencies = await agencyAPI.getAll();
          const currentAgency = agencies.find((a: any) => a.id === user.agency_id);
          if (currentAgency) {
            setAgency(currentAgency);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du logo de l\'agence:', error);
        }
      }
    };

    fetchAgencyLogo();
  }, [user?.agency_id]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('🏠 Logo cliqué dans Sidebar - Navigation vers accueil');
    navigate('/');
    onClose();
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 bg-gray-900/80 lg:hidden ${
          isOpen ? 'block' : 'hidden'
        }`}
        onClick={onClose}
      />

      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white px-4 pb-4 dark:bg-gray-800 lg:block lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out`}
      >
        <div className="flex h-16 shrink-0 items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogoClick}
              className="flex items-center focus:outline-none hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="Retour à l'accueil"
            >
              <img
                src="https://image.noelshack.com/fichiers/2025/08/1/1739816442-red-immo-3.png"
                alt="RED IMMO"
                className="h-21 ml-[-35px] mt-[15px]"
              />
            </button>
          </div>
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-200 lg:hidden"
            onClick={onClose}
          >
            <span className="sr-only">Close sidebar</span>
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <nav className="mt-8 flex flex-col h-[calc(100vh-8rem)]">
          <ul className="space-y-2 flex-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    )}
                  >
                    <Icon className={cn(
                      "h-4 w-4",
                      isActive
                        ? "text-primary-600 dark:text-primary-400"
                        : "text-[#0a8345]"
                    )} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
};